import React, { useState, useEffect } from "react";
import { Card, Container, Row, Col, Table, ListGroup, Spinner, Alert, Badge, Button } from "react-bootstrap";
import { FaUsers, FaBox, FaDollarSign, FaShoppingCart, FaClipboardList, FaBell, FaTasks, FaUserCog } from "react-icons/fa";
import { Link } from "react-router-dom";
import "./AdminDashboard.css";

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProducts: 0,
    totalOrders: 0,
    totalServiceRequests: 0,
    totalTechnicians: 0,
    totalRevenue: 0,
    pendingOrders: 0,
    completedOrders: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentServiceRequests, setRecentServiceRequests] = useState([]);

  // Fetch dashboard stats from the backend
  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const isStaticAdmin = localStorage.getItem("adminAuthenticated") === "true";
      const adminEmail = localStorage.getItem("adminEmail");
      
      // Prepare headers based on authentication type
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (isStaticAdmin) {
        headers['x-static-admin-email'] = adminEmail || 'admin@madhu.com';
        headers['x-static-admin-auth'] = 'true';
        headers['Authorization'] = `StaticAdmin ${adminEmail || 'admin@madhu.com'}`;
      } else {
        const token = localStorage.getItem("token");
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      let url = "https://madhusewingmachines.com/api/admin/dashboard";
      if (isStaticAdmin) {
        url += `?staticAdminEmail=${encodeURIComponent(adminEmail || 'admin@madhu.com')}&staticAdminAuth=true`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Failed to fetch: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Handle both old and new response formats
      if (data.stats) {
        setStats(data.stats);
        setRecentOrders(data.recentOrders || []);
        setRecentServiceRequests(data.recentServiceRequests || []);
      } else {
        // Old format
        setStats({
          totalUsers: data.totalUsers || 0,
          totalProducts: data.totalProducts || 0,
          totalOrders: data.totalOrders || 0,
          totalServiceRequests: data.totalServiceRequests || 0,
          totalTechnicians: 0,
          totalRevenue: 0,
          pendingOrders: 0,
          completedOrders: 0
        });
        setRecentOrders(data.recentOrders || []);
        setRecentServiceRequests(data.recentServiceRequests || []);
      }
    } catch (err) {
      console.error('❌ Fetch dashboard stats error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    const statusColors = {
      'Pending': 'warning',
      'Processing': 'info',
      'Shipped': 'primary',
      'Delivered': 'success',
      'Cancelled': 'danger',
      'Completed': 'success'
    };
    return statusColors[status] || 'secondary';
  };

  if (loading) {
    return (
      <Container fluid className="p-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger">Error: {error}</Alert>
      </Container>
    );
  }

  const statsCards = [
    { 
      title: "Total Users", 
      value: stats.totalUsers.toLocaleString(), 
      icon: <FaUsers />, 
      color: "primary",
      link: "/admin/users"
    },
    { 
      title: "Total Products", 
      value: stats.totalProducts.toLocaleString(), 
      icon: <FaBox />, 
      color: "success",
      link: "/admin/products"
    },
    { 
      title: "Total Revenue", 
      value: formatCurrency(stats.totalRevenue), 
      icon: <FaDollarSign />, 
      color: "danger",
      link: "/admin/orders"
    },
    { 
      title: "Total Orders", 
      value: stats.totalOrders.toLocaleString(), 
      icon: <FaShoppingCart />, 
      color: "warning",
      link: "/admin/orders"
    },
    { 
      title: "Service Requests", 
      value: stats.totalServiceRequests.toLocaleString(), 
      icon: <FaClipboardList />, 
      color: "info",
      link: "/admin/serviceRequest"
    },
    { 
      title: "Technicians", 
      value: stats.totalTechnicians.toLocaleString(), 
      icon: <FaUserCog />, 
      color: "secondary",
      link: "/admin/technicians"
    },
  ];

  return (
    <Container fluid className="p-4">
      <h1 className="dashboard-title mb-4">Admin Dashboard</h1>

      {/* Statistics Cards */}
      <Row className="mb-4">
        {statsCards.map((stat, index) => (
          <Col key={index} md={6} lg={4} className="mb-3">
            <Link to={stat.link} style={{ textDecoration: 'none', color: 'inherit' }}>
              <Card className={`stat-card bg-${stat.color} text-white h-100`} style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
              <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <Card.Title className="mb-2">{stat.title}</Card.Title>
                      <h3 className="mb-0">{stat.value}</h3>
                    </div>
                    <div className="stat-icon" style={{ fontSize: '2.5rem', opacity: 0.8 }}>
                      {stat.icon}
                    </div>
                  </div>
              </Card.Body>
            </Card>
            </Link>
          </Col>
        ))}
      </Row>

      {/* Additional Stats Row */}
      <Row className="mb-4">
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Pending Orders</h6>
                  <h4 className="mb-0 text-warning">{stats.pendingOrders}</h4>
                </div>
                <FaTasks size={30} className="text-warning" />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Completed Orders</h6>
                  <h4 className="mb-0 text-success">{stats.completedOrders}</h4>
                </div>
                <FaShoppingCart size={30} className="text-success" />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Orders & Service Requests */}
      <Row className="mb-4">
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Recent Orders</span>
              <Link to="/admin/orders">
                <Button variant="link" size="sm" className="p-0">View All</Button>
              </Link>
            </Card.Header>
            <Card.Body>
              {recentOrders.length === 0 ? (
                <Alert variant="info" className="mb-0">No recent orders</Alert>
              ) : (
                <Table striped bordered hover responsive size="sm">
                <thead>
                  <tr>
                      <th>Order #</th>
                    <th>Customer</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((order) => (
                      <tr key={order._id}>
                        <td>#{order.orderNumber || order._id.substring(0, 8)}</td>
                        <td>{order.fullName || order.userId?.name || 'N/A'}</td>
                        <td>₹{order.total || 0}</td>
                        <td>
                          <Badge bg={getStatusBadge(order.status)}>
                            {order.status || 'Pending'}
                          </Badge>
                        </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Recent Service Requests</span>
              <Link to="/admin/serviceRequest">
                <Button variant="link" size="sm" className="p-0">View All</Button>
              </Link>
            </Card.Header>
            <Card.Body>
              {recentServiceRequests.length === 0 ? (
                <Alert variant="info" className="mb-0">No recent service requests</Alert>
              ) : (
                <ListGroup variant="flush">
                  {recentServiceRequests.map((request) => (
                    <ListGroup.Item key={request._id} className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div className="fw-bold">{request.user?.name || request.fullName || 'N/A'}</div>
                        <small className="text-muted">{request.productName || request.product?.name || 'Service Request'}</small>
                        <div className="mt-1">
                          <Badge bg={request.status === 'Completed' ? 'success' : 'warning'}>
                            {request.status || 'Pending'}
                          </Badge>
                        </div>
                      </div>
                      <small className="text-muted">{formatDate(request.createdAt)}</small>
                  </ListGroup.Item>
                ))}
              </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminDashboard;
